import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { gdprAPI, formatApiError } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, Download, Trash, ShieldCheck, WarningCircle, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function PrivacySettingsPage() {
  const { user, refreshUser } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [consent, setConsent] = useState(null);
  const [deletionStatus, setDeletionStatus] = useState(null);
  const [loading, setLoading] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [c, d] = await Promise.all([
        gdprAPI.getConsent(),
        gdprAPI.getDeletionStatus(),
      ]);
      setConsent(c.data);
      setDeletionStatus(d.data);
    } catch (err) { /* silent */ }
  };

  const handleExport = async () => {
    setLoading('export');
    try {
      const { data } = await gdprAPI.exportData();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `divispend_data_${user?.user_id}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Data exported successfully');
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(''); }
  };

  const handleDeleteRequest = async () => {
    if (!window.confirm('Are you sure? Your account will be permanently deleted in 30 days. You can cancel within that period.')) return;
    setLoading('delete');
    try {
      await gdprAPI.requestDeletion();
      toast.success('Deletion request submitted');
      loadData();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(''); }
  };

  const handleCancelDeletion = async () => {
    setLoading('cancel');
    try {
      await gdprAPI.cancelDeletion();
      toast.success('Deletion cancelled');
      loadData();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(''); }
  };

  const toggleConsent = async (key, value) => {
    try {
      await gdprAPI.updateConsent({ [key]: value });
      toast.success('Preferences updated');
      loadData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0A0A0A]' : 'bg-[#F5F5F5]'}`}>
      <header className={`sticky top-0 z-20 border-b ${isDark ? 'bg-[#0A0A0A]/90 border-[#222222]' : 'bg-white/90 border-[#E5E5E5]'} backdrop-blur-md`}>
        <div className="max-w-xl mx-auto px-4 h-14 flex items-center gap-3">
          <Link to="/profile" className="text-[#999999] hover:text-[#22C55E]"><ArrowLeft className="w-4 h-4" /></Link>
          <ShieldCheck className="w-5 h-5 text-[#22C55E]" weight="fill" />
          <h1 className={`font-bold ${isDark ? 'text-white' : 'text-[#111111]'}`} data-testid="privacy-title">Privacy & Data</h1>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-4 py-6 space-y-4">
        {/* Export Data */}
        <Card className={isDark ? 'bg-[#111111] border-[#222222]' : 'bg-white border-[#E5E5E5]'}>
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <h3 className={`font-bold ${isDark ? 'text-white' : 'text-[#111111]'}`}>Download My Data</h3>
                <p className="text-xs text-[#999999] mt-1">Export all your data including profile, transactions, products, reviews, and messages as a JSON file.</p>
              </div>
              <Button onClick={handleExport} disabled={loading === 'export'} variant="outline" className="border-[#333333] text-[#22C55E] shrink-0" data-testid="export-data-btn">
                <Download className="w-4 h-4 mr-2" />
                {loading === 'export' ? 'Exporting...' : 'Export'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Consent */}
        {consent && (
          <Card className={isDark ? 'bg-[#111111] border-[#222222]' : 'bg-white border-[#E5E5E5]'}>
            <CardContent className="p-5 space-y-3">
              <h3 className={`font-bold ${isDark ? 'text-white' : 'text-[#111111]'}`}>Consent Preferences</h3>

              <div className="flex items-center justify-between py-2">
                <div>
                  <p className={`text-sm ${isDark ? 'text-white' : 'text-[#111111]'}`}>Data Processing</p>
                  <p className="text-xs text-[#999999]">Required for core app functionality</p>
                </div>
                <button
                  onClick={() => toggleConsent('data_processing_consent', !consent.data_processing_consent)}
                  className={`w-11 h-6 rounded-full transition ${consent.data_processing_consent ? 'bg-[#22C55E]' : 'bg-[#333333]'}`}
                  data-testid="consent-data-toggle"
                >
                  <div className={`w-5 h-5 rounded-full bg-white transition-transform ${consent.data_processing_consent ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </button>
              </div>

              <div className="flex items-center justify-between py-2 border-t border-[#222222]">
                <div>
                  <p className={`text-sm ${isDark ? 'text-white' : 'text-[#111111]'}`}>Marketing Communications</p>
                  <p className="text-xs text-[#999999]">Receive promotional emails and updates</p>
                </div>
                <button
                  onClick={() => toggleConsent('marketing_consent', !consent.marketing_consent)}
                  className={`w-11 h-6 rounded-full transition ${consent.marketing_consent ? 'bg-[#22C55E]' : 'bg-[#333333]'}`}
                  data-testid="consent-marketing-toggle"
                >
                  <div className={`w-5 h-5 rounded-full bg-white transition-transform ${consent.marketing_consent ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Delete Account */}
        <Card className={`${isDark ? 'bg-[#111111]' : 'bg-white'} border-red-500/20`}>
          <CardContent className="p-5">
            {deletionStatus?.pending_deletion ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <WarningCircle className="w-5 h-5 text-red-400" weight="fill" />
                  <h3 className="text-red-400 font-bold">Deletion Scheduled</h3>
                </div>
                <p className="text-xs text-[#999999]">
                  Your account is scheduled for deletion on {new Date(deletionStatus.request.scheduled_deletion).toLocaleDateString()}.
                  All data will be permanently removed.
                </p>
                <Button onClick={handleCancelDeletion} disabled={loading === 'cancel'} variant="outline" className="border-[#333333] text-white" data-testid="cancel-deletion-btn">
                  {loading === 'cancel' ? 'Cancelling...' : 'Cancel Deletion'}
                </Button>
              </div>
            ) : (
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-red-400 font-bold">Delete My Account</h3>
                  <p className="text-xs text-[#999999] mt-1">Permanently delete your account and all data. You have 30 days to change your mind. Balance must be $0.</p>
                </div>
                <Button onClick={handleDeleteRequest} disabled={loading === 'delete'} className="bg-red-600 hover:bg-red-700 text-white shrink-0" data-testid="delete-account-btn">
                  <Trash className="w-4 h-4 mr-2" />
                  {loading === 'delete' ? 'Requesting...' : 'Delete'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
